import {
  Dropdown,
  Option,
  OptionGroup,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { truncateString } from "../PromptFilter/PromptFilter";
import { useStyles } from "../PromptFilter/PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilter/PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "../PromptFilter/usePromptFilterStrings";
import type { Appearance } from "../data";
import type { Filter, FilterOption } from "./types";
import { groupFilters } from "./utils";

export type Props = {
  /**
   * Optional appearance to be applied to control icon style
   */
  appearance?: Appearance;

  /**
   * The id of the FluentUI tab that's hosting this filter.
   */
  tab: string;

  /**
   * Filter for this dropdown.
   */
  filter: Filter;

  /**
   * Default selected values for this dropdown.
   */
  defaultSelectedValues?: string[];

  /**
   * Selected values for this dropdown. It is used to fully control the dropdown.
   */
  selectedValues?: string[];

  /**
   * Optional loading state to be applied to the root element. If true, a skeleton view will be rendered.
   */
  isLoading?: boolean;

  /**
   * Callback for changed event.
   */
  onChange: (ev: SelectionEvents, newValue: string[] | undefined) => void;
};

/**
 * Render a list of options under a dropdown. If groupKey is not "NA", render a grouped options otherwise regular options.
 * @param groupKey group key of the options. By default it would "NA" which means the list of options doesn't belong to any group.
 * @param groupLabel group label.
 * @param filterOptions list of filter options.
 * @param selectedValues list of selected values, used to set correct aria-checked on each option.
 * @param valueToLabel function that converts option's value to a proper label.
 * @returns a list of options under a dropdown.
 */
const renderOptions = (
  groupKey: string,
  groupLabel: string,
  filterOptions: FilterOption[],
  selectedValues: string[],
  valueToLabel: (value: string) => string
) => {
  const options = filterOptions.map((x) => (
    <Option
      text={x.value}
      value={x.value}
      key={x.value}
      aria-label={`${valueToLabel(x.value)}-checkbox`}
      aria-checked={selectedValues.includes(x.value)}
    >
      {valueToLabel(x.value)}
    </Option>
  ));

  if (groupKey === "NA") {
    return <>{options}</>;
  } else {
    return <OptionGroup label={groupLabel}>{options}</OptionGroup>;
  }
};

export const DropdownFilter: React.FC<Props> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
}) => {
  const styles = useStyles(appearance)();
  const [values, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );
  const { facetKeyToLabel, facetValueToLabel, groupValueToLabel } =
    usePromptFilterLocalizations();
  const truncateStringMaxWidth = appearance === "single-col" ? 100 : 180;
  const filterLabel = facetKeyToLabel(filter.key);
  const filterByGroup = groupFilters(filter);

  const onChanged = (_: SelectionEvents, data: OptionOnSelectData): void => {
    setValues(data.selectedOptions);
  };

  if (filter.options.length < 2) {
    return null;
  }

  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Dropdown
          data-testid={`${filter.key}-dropdown`}
          aria-label={filterLabel}
          className={styles.dropdown}
          multiselect={!filter.isSingleSelect}
          placeholder={filterLabel}
          value={
            values &&
            truncateString(
              values
                .map((value: string) => facetValueToLabel(value))
                .join(", "),
              truncateStringMaxWidth
            )
          }
          selectedOptions={values ?? []}
          onOptionSelect={onChanged}
        >
          {Object.entries(filterByGroup).map(([groupKey, filterOptions]) => (
            <React.Fragment key={groupKey}>
              {renderOptions(
                groupKey,
                groupValueToLabel(groupKey),
                filterOptions,
                values ?? [],
                facetValueToLabel
              )}
            </React.Fragment>
          ))}
        </Dropdown>
      )}
    </React.Fragment>
  );
};
